'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  miles: 300,
  gallons: 10,
  gasPrice: 3.5,
};

const PRESETS = {
  conservative: {
    miles: 200,
    gallons: 8,
    gasPrice: 3.0,
  },
  moderate: {
    miles: 300,
    gallons: 10,
    gasPrice: 3.5,
  },
  aggressive: {
    miles: 500,
    gallons: 20,
    gasPrice: 4.5,
  },
};

export default function CalculatorGasoline() {
  const [miles, setMiles] = useState(INITIAL_STATE.miles);
  const [gallons, setGallons] = useState(INITIAL_STATE.gallons);
  const [gasPrice, setGasPrice] = useState(INITIAL_STATE.gasPrice);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: number) => void, value: number) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const mpg = miles / gallons;
  const tripCost = gallons * gasPrice;
  const costPerMile = tripCost / miles;

  const handleReset = () => {
    setMiles(INITIAL_STATE.miles);
    setGallons(INITIAL_STATE.gallons);
    setGasPrice(INITIAL_STATE.gasPrice);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setMiles(values.miles);
    setGallons(values.gallons);
    setGasPrice(values.gasPrice);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Miles Driven</label>
            <input
              type="number"
              value={miles}
              onChange={(e) => handleChange(setMiles, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Gallons Used</label>
            <input
              type="number"
              value={gallons}
              onChange={(e) => handleChange(setGallons, Number(e.target.value))}
              step={0.1}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Gas Price per Gallon</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={gasPrice}
                onChange={(e) => handleChange(setGasPrice, Number(e.target.value))}
                step={0.1}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Fuel Efficiency</div>
            <div className="text-5xl font-bold">{mpg.toFixed(1)} MPG</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Trip Cost</div>
              <div className="text-xl font-bold text-slate-900">${tripCost.toFixed(2)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Cost per Mile</div>
              <div className="text-xl font-bold text-slate-900">${costPerMile.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}